import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowRight, FaLaptopCode, FaUsers, FaRocket, FaGraduationCap,
  FaMapMarkerAlt, FaClock, FaTimes, FaCheck, FaChevronDown, FaChevronUp,
  FaBriefcase, FaRupeeSign,
} from 'react-icons/fa';
import { HiArrowRight, HiSparkles, HiCheckCircle } from 'react-icons/hi';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import axios from 'axios';
import useHeroEntrance from '../hooks/useHeroEntrance';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const Career = () => {
  const processRef = useRef(null);
  const heroBadgeRef = useRef(null);
  const heroH1Ref = useRef(null);
  const heroParaRef = useRef(null);
  const heroCtaRef = useRef(null);
  const heroImgRef = useRef(null);
  useHeroEntrance([
    heroBadgeRef,
    { ref: heroH1Ref, heading: true },
    heroParaRef,
    heroCtaRef,
    heroImgRef,
  ]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedJob, setExpandedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', resume: null, coverLetter: '',
    experience: '', currentCompany: '', expectedSalary: '', noticePeriod: '',
  });
  const [submitStatus, setSubmitStatus] = useState({ loading: false, success: false, error: null });

  /* Benefits grid: alternating left/right slide-in with stagger */
  const benefitsGridRef = useRef(null);
  useEffect(() => {
    if (!benefitsGridRef.current) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.benefit-card');
      if (!cards.length) return;
      if (prefersReducedMotion()) {
        gsap.set(cards, { opacity: 1, x: 0, y: 0, scale: 1 });
        return;
      }
      cards.forEach((c, i) => {
        gsap.set(c, {
          opacity: 0,
          x: i % 2 === 0 ? -40 : 40,
          y: 24,
          scale: 0.95,
          willChange: 'transform',
        });
      });
      ScrollTrigger.create({
        trigger: benefitsGridRef.current,
        start: 'top 82%',
        once: true,
        onEnter: () => {
          gsap.to(cards, {
            opacity: 1, x: 0, y: 0, scale: 1,
            duration: 0.95, stagger: 0.1, ease: 'power3.out',
          });
        },
      });
    }, benefitsGridRef);
    return () => ctx.revert();
  }, []);

  /* ── Hiring steps: skew-shear slide-in + animated gradient sweep on numbers + 3D peel-lift hover ── */
  useEffect(() => {
    if (prefersReducedMotion()) return;
    // Inject styles for the gradient sweep on numbers (unique to this section)
    if (!document.getElementById('career-process-styles')) {
      const style = document.createElement('style');
      style.id = 'career-process-styles';
      style.textContent = `
        .process-step { transform-style: preserve-3d; backface-visibility: hidden; }
        .process-step .step-num {
          background: linear-gradient(110deg,
            #0984e3 0%, #00b0ff 25%, #ffffff 45%, #00b0ff 55%, #a800ff 75%, #0984e3 100%);
          background-size: 300% 100%;
          background-position: 200% 0%;
          -webkit-background-clip: text;
                  background-clip: text;
          -webkit-text-fill-color: transparent;
                  color: transparent;
          will-change: background-position;
        }
      `;
      document.head.appendChild(style);
    }

    const steps = Array.from(document.querySelectorAll('.process-step'));
    if (!steps.length) return;

    // Initial state: tall, skewed, transparent — like papers waiting to slide flat onto the table
    gsap.set(steps, {
      opacity: 0,
      y: 110,
      skewY: 9,
      rotateX: -22,
      transformOrigin: 'bottom center',
      transformPerspective: 1100,
      willChange: 'transform, opacity',
    });

    const entrance = ScrollTrigger.create({
      trigger: processRef.current,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        gsap.to(steps, {
          opacity: 1,
          y: 0,
          skewY: 0,
          rotateX: 0,
          duration: 1.15,
          ease: 'expo.out',
          stagger: 0.18,
        });

        // Animated shimmer sweep across each number — runs continuously after entrance
        steps.forEach((step, i) => {
          const num = step.querySelector('.step-num');
          if (!num) return;
          gsap.fromTo(num,
            { backgroundPosition: '200% 0%' },
            {
              backgroundPosition: '-100% 0%',
              duration: 3.4,
              repeat: -1,
              ease: 'none',
              delay: 0.6 + i * 0.18,
            }
          );
        });
      },
    });

    // 3D peel-lift hover: card tips forward as if peeling off the table
    const handlers = steps.map((step) => {
      const onEnter = () => gsap.to(step, {
        y: -10, rotateX: -6, scale: 1.025,
        boxShadow: '0 28px 50px -22px rgba(9,132,227,0.28), 0 14px 30px -18px rgba(168,0,255,0.16)',
        duration: 0.55, ease: 'power3.out', overwrite: 'auto',
      });
      const onLeave = () => gsap.to(step, {
        y: 0, rotateX: 0, scale: 1,
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        duration: 0.85, ease: 'power3.out', overwrite: 'auto',
      });
      step.addEventListener('mouseenter', onEnter);
      step.addEventListener('mouseleave', onLeave);
      return { step, onEnter, onLeave };
    });

    return () => {
      entrance.kill();
      gsap.killTweensOf(steps);
      handlers.forEach(({ step, onEnter, onLeave }) => {
        step.removeEventListener('mouseenter', onEnter);
        step.removeEventListener('mouseleave', onLeave);
      });
    };
  }, []);

  const benefits = [
    { icon: <FaLaptopCode />, title: 'Modern Tech Stack',      description: 'Work with the latest technologies including React, Node.js, and cloud platforms.', color: 'from-nexcess-blue to-nexcess-cyan',    tagline: 'Build with the best tools in the industry.' },
    { icon: <FaRocket />,     title: 'Growth Opportunities',   description: 'Clear career paths and opportunities to lead projects and teams.',                   color: 'from-nexcess-cyan to-nexcess-purple',  tagline: 'Your ambition, our runway.' },
    { icon: <FaGraduationCap />, title: 'Learning & Development', description: 'Regular training, workshops and conference attendance support.',                   color: 'from-nexcess-purple to-nexcess-blue',  tagline: 'Never stop growing.' },
    { icon: <FaUsers />,      title: 'Inclusive Culture',      description: 'A diverse and welcoming environment where every voice is valued.',                    color: 'from-nexcess-blue to-nexcess-purple',  tagline: 'Every perspective matters here.' },
  ];

  const openings = [
    {
      title: 'Frontend Developer', location: 'Remote', experience: '1+ years', department: 'development',
      skills: ['React', 'JavaScript', 'HTML/CSS', 'Responsive Design'],
      responsibilities: ['Build responsive and interactive user interfaces', 'Collaborate with designers and backend developers', 'Optimize applications for maximum performance', 'Implement UI/UX designs and wireframes'],
      qualifications: ["Bachelor's degree in Computer Science or related field", 'At least 1 year of experience with React.js', 'Proficiency in modern JavaScript (ES6+)', 'Strong problem-solving skills'],
      salary: '₹5-8 LPA', id: 'fd-001', postedDate: '2023-10-15',
    },
    {
      title: 'Backend Developer', location: 'Surat', experience: '2+ years', department: 'development',
      skills: ['Node.js', 'MongoDB', 'RESTful APIs', 'GraphQL'],
      responsibilities: ['Design and implement scalable backend services', 'Develop and maintain database schemas', 'Create and document RESTful APIs', 'Ensure security and performance of the backend'],
      qualifications: ["Bachelor's degree in Computer Science or related field", 'At least 2 years of experience with Node.js', 'Experience with MongoDB or similar NoSQL databases', 'Understanding of server-side templating languages'],
      salary: '₹6-10 LPA', id: 'bd-002', postedDate: '2023-10-12',
    },
    {
      title: 'UI/UX Designer', location: 'Hybrid', experience: '1-3 years', department: 'design',
      skills: ['Figma', 'User Research', 'Prototyping', 'Adobe XD'],
      responsibilities: ['Create wireframes, prototypes, and user flows', 'Conduct user research and usability testing', 'Collaborate with developers for implementation', 'Design responsive interfaces for web and mobile'],
      qualifications: ["Bachelor's degree in Design, HCI, or related field", 'Portfolio demonstrating UX design projects', 'Experience with Figma or similar design tools', 'Understanding of user-centered design principles'],
      salary: '₹5-9 LPA', id: 'ui-003', postedDate: '2023-10-18',
    },
    {
      title: 'DevOps Engineer', location: 'Remote', experience: '3+ years', department: 'devops',
      skills: ['AWS', 'Docker', 'CI/CD', 'Kubernetes'],
      responsibilities: ['Set up and maintain CI/CD pipelines', 'Manage cloud infrastructure on AWS', 'Implement container orchestration with Kubernetes', 'Automate deployment processes'],
      qualifications: ["Bachelor's degree in Computer Science or related field", 'At least 3 years of experience in DevOps', 'Experience with AWS and containerization', 'Knowledge of infrastructure as code'],
      salary: '₹12-18 LPA', id: 'do-004', postedDate: '2023-10-05',
    },
  ];

  const filteredJobs = activeFilter === 'all' ? openings : openings.filter((j) => j.department === activeFilter);

  // Stacking cards — re-init ScrollTriggers whenever filtered list changes
  useEffect(() => {
    if (prefersReducedMotion()) return;
    let triggers = [];
    const timer = setTimeout(() => {
      const cards = Array.from(document.querySelectorAll('.job-stack-card'));
      if (cards.length < 2) return;

      cards.forEach((card, i) => {
        gsap.set(card, { scale: 1, filter: 'brightness(1)' });
        if (i >= cards.length - 1) return;

        triggers.push(ScrollTrigger.create({
          trigger: cards[i + 1],
          start: 'top 90%',
          end: `top ${100 + i * 6}px`,
          scrub: true,
          onUpdate: ({ progress }) => {
            gsap.set(card, {
              scale: 1 - progress * 0.035,
              filter: `brightness(${1 - progress * 0.1})`,
            });
          },
          onLeaveBack: () => gsap.set(card, { scale: 1, filter: 'brightness(1)' }),
        }));
      });
    }, 350);

    return () => {
      clearTimeout(timer);
      triggers.forEach(t => t.kill());
      document.querySelectorAll('.job-stack-card').forEach(c =>
        gsap.set(c, { scale: 1, filter: 'brightness(1)' })
      );
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredJobs]);

  const hiringSteps = [
    { num: '01', title: 'Apply', description: 'Submit your application through our career portal. Our team reviews every application carefully.' },
    { num: '02', title: 'Initial Interview', description: 'HR screening and cultural fit discussion to get to know each other better.' },
    { num: '03', title: 'Technical Interview', description: 'In-depth technical discussion with the team to assess your skills and experience.' },
    { num: '04', title: 'Offer & Onboarding', description: 'Welcome to the Nexcess Infotech family! We\'ll get you set up and ready to make an impact.' },
  ];

  const departments = [
    { id: 'all', label: 'All Positions' },
    { id: 'development', label: 'Development' },
    { id: 'design', label: 'Design' },
    { id: 'devops', label: 'DevOps' },
  ];

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
    document.body.style.overflow = '';
    setFormData({ name: '', email: '', phone: '', resume: null, coverLetter: '', experience: '', currentCompany: '', expectedSalary: '', noticePeriod: '' });
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume' && files && files[0]) {
      const file = files[0];
      const fileSize = file.size / 1024 / 1024;
      if (fileSize > 5) {
        setSubmitStatus({ loading: false, success: false, error: `File size exceeds 5MB (${fileSize.toFixed(2)}MB). Please select a smaller file.` });
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData((prev) => ({ ...prev, resumeData: event.target.result }));
        if (submitStatus.error && submitStatus.error.includes('File size exceeds')) {
          setSubmitStatus({ loading: false, success: false, error: null });
        }
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({ ...prev, [name]: files ? files[0] : value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, success: false, error: null });
    try {
      const applicationData = { ...formData, jobTitle: selectedJob?.title || 'Your Application' };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/send-job-application`, applicationData);
      if (response.status === 200) {
        setSubmitStatus({ loading: false, success: true, error: null });
        setTimeout(() => {
          handleCloseModal();
          setTimeout(() => setSubmitStatus({ loading: false, success: false, error: null }), 500);
        }, 3000);
      }
    } catch (error) {
      setSubmitStatus({ loading: false, success: false, error: error.response?.data?.error || 'Failed to submit application. Please try again.' });
    }
  };

  const locationColor = (loc) => {
    if (loc === 'Remote') return 'bg-green-50 text-green-600 border-green-200';
    if (loc === 'Hybrid') return 'bg-blue-50 text-blue-600 border-blue-200';
    return 'bg-orange-50 text-orange-600 border-orange-200';
  };

  return (
    <>
      {/* ───── HERO ───── */}
      <section className="relative pt-36 pb-16 overflow-hidden bg-paper">
        <div className="absolute inset-0 bg-mesh-soft pointer-events-none" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-nexcess-blue/8 blur-3xl animate-blob pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-nexcess-purple/8 blur-3xl animate-blob pointer-events-none" style={{ animationDelay: '4s' }} />

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div ref={heroBadgeRef} className="badge mb-5">
                <HiSparkles /> We're Hiring
              </div>
              <h1 ref={heroH1Ref} className="display-hero mb-5">
                Shape the <span className="gradient-text">Future</span> with Nexcess Infotech.
              </h1>
              <p ref={heroParaRef} className="narrative-lg mb-8">
                We're building innovative digital solutions — come be part of it. Join our team
                of talented professionals and make an impact.
              </p>
              <div ref={heroCtaRef} className="flex flex-wrap gap-3">
                <button
                  onClick={() => document.getElementById('openings').scrollIntoView({ behavior: 'smooth' })}
                  className="btn btn-primary"
                >
                  View Open Roles <HiArrowRight />
                </button>
                <button onClick={() => navigate('/about')} className="btn btn-outline">
                  Learn About Us
                </button>
              </div>
            </div>

            {/* Right — hero image with floating badges */}
            <div ref={heroImgRef} className="relative">
              <div className="rounded-3xl overflow-hidden shadow-soft-lg" style={{ aspectRatio: '4/3' }}>
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80"
                  alt="Team collaboration"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              {/* Floating cards */}
              <motion.div
                className="absolute -top-5 -left-5 glass rounded-2xl px-5 py-4 shadow-soft-lg"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity }}
              >
                <div className="text-2xl font-extrabold gradient-text">204+</div>
                <div className="text-xs text-ink-soft font-semibold">Projects Delivered</div>
              </motion.div>
              <motion.div
                className="absolute -bottom-5 -right-5 glass rounded-2xl px-5 py-4 shadow-soft-lg"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 6, repeat: Infinity, delay: 2 }}
              >
                <div className="flex items-center gap-2 text-green-500 text-sm font-bold mb-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  4 Open Roles
                </div>
                <div className="text-xs text-ink-soft">Apply today</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── BENEFITS ───── */}
      <section className="section-padding bg-paper-2">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Why Join Us</div>
            <h2 data-reveal="wipe" className="heading-xl mb-5">
              Benefits of Working at <span className="gradient-text">Nexcess Infotech</span>
            </h2>
            <p data-reveal="up" data-reveal-delay="0.2" className="narrative-lg">
              We value our employees and strive to create an environment where you can thrive
              professionally and personally.
            </p>
          </div>

          <div ref={benefitsGridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="benefit-card group"
                style={{ perspective: '1000px', height: '240px', willChange: 'transform' }}
              >
                {/* Flip container */}
                <div
                  className="relative w-full h-full transition-transform duration-700"
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: 'rotateY(0deg)',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'rotateY(180deg)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'rotateY(0deg)'}
                >
                  {/* ── FRONT ── */}
                  <div
                    className="absolute inset-0 bg-white rounded-2xl p-7 border border-hairline shadow-soft flex flex-col"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center text-white text-2xl mb-5 shadow-soft`}>
                      {benefit.icon}
                    </div>
                    <h3 className="font-bold text-ink mb-2 text-lg">{benefit.title}</h3>
                    <p className="text-sm text-ink-soft leading-relaxed line-clamp-2">{benefit.description}</p>
                  </div>

                  {/* ── BACK ── */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${benefit.color} rounded-2xl p-7 flex flex-col items-center justify-center text-white text-center shadow-soft-lg`}
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <div className="text-5xl mb-5 opacity-90">{benefit.icon}</div>
                    <h3 className="font-extrabold text-lg mb-3 tracking-tight">{benefit.title}</h3>
                    <p className="text-sm text-white/80 leading-relaxed">{benefit.tagline}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── JOB OPENINGS ───── */}
      <section id="openings" className="section-padding bg-paper">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Open Roles</div>
            <h2 data-reveal="wipe" className="heading-xl mb-5">
              Current Job <span className="gradient-text">Openings</span>
            </h2>
            <p data-reveal="up" data-reveal-delay="0.2" className="narrative-lg">Find your perfect role and join our talented team of professionals.</p>
          </div>

          {/* Filter pills */}
          <div className="flex justify-center mb-10">
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {departments.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setActiveFilter(d.id)}
                  className={`px-5 py-2.5 text-sm font-semibold rounded-full border transition-all duration-300 ${
                    activeFilter === d.id
                      ? 'text-white border-transparent shadow-soft'
                      : 'bg-white text-ink-soft border-hairline hover:text-nexcess-blue hover:border-nexcess-blue/40'
                  }`}
                  style={
                    activeFilter === d.id
                      ? { background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 60%, #d500f9 100%)' }
                      : {}
                  }
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="popLayout">
            <div className="space-y-3 pb-[60vh]">
              {filteredJobs.length > 0 ? (
                filteredJobs.map((job, index) => (
                  <motion.div
                    key={job.id}
                    layout
                    initial={{ opacity: 0, y: 20, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.92, filter: 'blur(4px)' }}
                    transition={{ delay: index * 0.08, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="job-stack-card bg-white rounded-2xl border border-hairline shadow-soft overflow-hidden hover:shadow-soft-lg transition-shadow duration-300"
                    style={{
                      position: 'sticky',
                      top: `${100 + index * 6}px`,
                      zIndex: index + 1,
                      transformOrigin: 'top center',
                    }}
                  >
                    {/* Card header */}
                    <div className="p-6 md:p-8">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <span className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold">
                              {job.department}
                            </span>
                            <span className="text-ink-soft">·</span>
                            <span className="text-xs text-ink-soft">
                              Posted {new Date(job.postedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold text-ink mb-3">{job.title}</h3>
                          <div className="flex flex-wrap items-center gap-3">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${locationColor(job.location)}`}>
                              <FaMapMarkerAlt size={10} /> {job.location}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-paper-2 text-ink-soft border-hairline">
                              <FaClock size={10} /> {job.experience}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-paper-2 text-ink-soft border-hairline">
                              <FaRupeeSign size={10} /> {job.salary}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                            className="btn btn-outline flex items-center gap-2"
                          >
                            {expandedJob === job.id ? <><FaChevronUp size={12} /> Less</> : <><FaChevronDown size={12} /> Details</>}
                          </button>
                          <button
                            onClick={() => handleApplyClick(job)}
                            className="btn btn-primary"
                          >
                            Apply Now <HiArrowRight />
                          </button>
                        </div>
                      </div>

                      {/* Skills */}
                      <div className="mt-5 flex flex-wrap gap-2">
                        {job.skills.map((s) => (
                          <span key={s} className="text-xs px-3 py-1.5 rounded-md bg-paper-2 text-ink-soft border border-hairline font-medium">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Expandable details */}
                    <AnimatePresence>
                      {expandedJob === job.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="border-t border-hairline mx-6 md:mx-8" />
                          <div className="p-6 md:p-8 grid md:grid-cols-2 gap-8">
                            <div>
                              <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-4">Responsibilities</div>
                              <ul className="space-y-2.5">
                                {job.responsibilities.map((r, i) => (
                                  <li key={i} className="text-sm text-ink-soft flex items-start gap-2">
                                    <FaCheck className="text-nexcess-blue mt-1 shrink-0" size={11} />
                                    <span>{r}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-4">Qualifications</div>
                              <ul className="space-y-2.5">
                                {job.qualifications.map((q, i) => (
                                  <li key={i} className="text-sm text-ink-soft flex items-start gap-2">
                                    <FaCheck className="text-nexcess-purple mt-1 shrink-0" size={11} />
                                    <span>{q}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              ) : (
                <div className="bg-white rounded-2xl border border-hairline shadow-soft p-14 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-paper-2 border border-hairline flex items-center justify-center mx-auto mb-5">
                    <FaBriefcase className="text-ink-soft text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-ink mb-2">No positions found</h3>
                  <p className="narrative-body mb-6">We don't have any open positions matching this filter right now.</p>
                  <button onClick={() => setActiveFilter('all')} className="btn btn-primary">
                    View All Positions
                  </button>
                </div>
              )}
            </div>
          </AnimatePresence>

          <div className="text-center mt-10">
            <p className="narrative-body mb-3">Don't see a role that fits your skills?</p>
            <Link to="/contact" className="editorial-link inline-flex items-center gap-1">
              Contact us about future opportunities <FaArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* ───── HIRING PROCESS ───── */}
      <section ref={processRef} className="section-padding bg-paper-2">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Our Process</div>
            <h2 data-reveal="wipe" className="heading-xl mb-5">
              Our Hiring <span className="gradient-text">Process</span>
            </h2>
            <p data-reveal="up" data-reveal-delay="0.2" className="narrative-lg">Here's what to expect when you apply to join our team.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {hiringSteps.map((step, index) => (
              <div
                key={index}
                className="process-step opacity-0 translate-y-4 relative bg-white rounded-2xl p-7 border border-hairline shadow-soft"
              >
                {/* Connector line */}
                {index < hiringSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 -right-3 w-6 h-0.5 bg-gradient-to-r from-nexcess-blue to-nexcess-cyan z-10" />
                )}
                <div className="step-num text-5xl font-extrabold leading-none mb-5">
                  {step.num}
                </div>
                <h3 className="font-bold text-ink mb-2 text-lg">{step.title}</h3>
                <p className="text-sm text-ink-soft leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── CTA ───── */}
      <section className="py-20 md:py-28 bg-paper">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-16 text-center text-white shadow-soft-lg" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 50%, #d500f9 100%)' }}>
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs uppercase tracking-wider font-semibold mb-6">
                <HiSparkles /> Open Application
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-5 text-white">
                Ready to Join Our Team?
              </h2>
              <p className="text-white/90 text-lg mb-9 max-w-xl mx-auto">
                We're always looking for passionate and talented individuals to join our growing team.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => handleApplyClick({ title: 'Open Application' })}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-nexcess-blue font-semibold shadow-soft hover:-translate-y-0.5 hover:shadow-2xl transition-all duration-300"
                >
                  Submit Your Application <HiArrowRight />
                </button>
                <a
                  href="mailto:careers@nexcessinfotech.com"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white/10 backdrop-blur-sm text-white border border-white/30 font-semibold hover:bg-white/20 hover:-translate-y-0.5 transition-all duration-300"
                >
                  careers@nexcessinfotech.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── APPLICATION MODAL ───── */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-soft-lg border border-hairline"
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="sticky top-0 bg-white rounded-t-3xl z-10 px-8 pt-8 pb-6 border-b border-hairline">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="badge mb-2">Job Application</div>
                    <h3 className="text-2xl font-bold text-ink">
                      Apply for <span className="gradient-text">{selectedJob?.title || 'Open Application'}</span>
                    </h3>
                  </div>
                  <button
                    onClick={handleCloseModal}
                    className="w-10 h-10 rounded-full bg-paper-2 border border-hairline flex items-center justify-center text-ink-soft hover:text-ink hover:border-nexcess-blue/40 transition-colors"
                    aria-label="Close"
                  >
                    <FaTimes size={16} />
                  </button>
                </div>
              </div>

              <div className="p-8">
                {submitStatus.success ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="rounded-2xl border border-green-200 bg-green-50 p-12 text-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white mx-auto mb-5 shadow-soft">
                      <HiCheckCircle size={32} />
                    </div>
                    <h4 className="text-2xl font-bold text-ink mb-2">Application Submitted!</h4>
                    <p className="text-ink-soft">Thank you for your interest. We'll contact you shortly about the next steps.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-2">Full Name *</label>
                        <input type="text" name="name" required minLength="2" value={formData.name} onChange={handleInputChange} className="input-field" placeholder="John Doe" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-2">Email *</label>
                        <input type="email" name="email" required value={formData.email} onChange={handleInputChange} className="input-field" placeholder="john@example.com" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-2">Phone *</label>
                        <input type="tel" name="phone" required pattern="^\d{10}$" value={formData.phone} onChange={handleInputChange} className="input-field" placeholder="10-digit number" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-2">Years of Experience *</label>
                        <input type="text" name="experience" required pattern="^\d+$" value={formData.experience} onChange={handleInputChange} className="input-field" placeholder="e.g. 2" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-2">Current Company</label>
                        <input type="text" name="currentCompany" value={formData.currentCompany} onChange={handleInputChange} className="input-field" placeholder="Company name" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-2">Expected Salary</label>
                        <input type="text" name="expectedSalary" value={formData.expectedSalary} onChange={handleInputChange} className="input-field" placeholder="e.g. ₹8 LPA" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-2">Notice Period (Days)</label>
                        <input type="text" name="noticePeriod" pattern="^\d+$" value={formData.noticePeriod} onChange={handleInputChange} className="input-field" placeholder="e.g. 30" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-2">Resume * <span className="text-ink-soft font-normal">(PDF/DOC, max 5MB)</span></label>
                        <input type="file" name="resume" required accept=".pdf,.doc,.docx" onChange={handleInputChange} className="input-field text-sm file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-nexcess-blue/10 file:text-nexcess-blue" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-ink mb-2">Cover Letter</label>
                      <textarea name="coverLetter" value={formData.coverLetter} onChange={handleInputChange} rows="5" className="input-field resize-none" placeholder="Tell us why you're interested in this position..." />
                    </div>

                    {submitStatus.error && (
                      <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 p-4 text-sm">
                        {submitStatus.error}
                      </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t border-hairline">
                      <button type="button" onClick={handleCloseModal} disabled={submitStatus.loading} className="btn btn-outline">Cancel</button>
                      <button type="submit" disabled={submitStatus.loading} className="btn btn-primary">
                        {submitStatus.loading ? 'Submitting…' : 'Submit Application'} <HiArrowRight />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Career;
